if (!customElements.get('products-toolbar')) {
  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      this.desktopDrawerTransitionMs = 300;
      this.init();
    }

    /**
     * Below lg the bar sits above the grid and pins under the header once it
     * is scrolled past. CSS sticky cannot do it here: body is a scroll
     * container (overflow-x hidden with html clipped), so sticky never engages.
     * A spacer holds the bar's place while it is pinned, so the page does not
     * jump by its height.
     */
    connectedCallback() {
      // A swapped-in copy of the markup can carry the class from the old node.
      this.classList.remove('is-pinned');
      if (!this.spacer) {
        this.spacer = document.createElement('div');
        this.spacer.className = 'products-toolbar__spacer';
      }
      this.before(this.spacer);
      this.onScroll = this.onScroll || (() => {
        if (this.pinFrame) return;
        this.pinFrame = requestAnimationFrame(() => {
          this.pinFrame = null;
          this.updatePin();
        });
      });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      // The page can change height without scrolling - a search collapsing to
      // no results under a restored scroll position left the bar pinned over a
      // gap - so a layout change re-checks too.
      this.layoutObserver = this.layoutObserver || new ResizeObserver(this.onScroll);
      this.layoutObserver.observe(document.body);
      this.updatePin();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.layoutObserver?.disconnect();
      this.spacer?.remove();
    }

    /**
     * Bottom edge of what is pinned at the top: the header when it is fixed or
     * sticky, and the B2B bar when it is fixed under it (phones, custom-topbar.css).
     */
    static pinnedBottom() {
      let bottom = 0;
      ['.cc-header', '.b2b-topbar-notice'].forEach((selector) => {
        const el = document.querySelector(selector);
        if (el && /fixed|sticky/.test(getComputedStyle(el).position)) {
          bottom = Math.max(bottom, el.getBoundingClientRect().bottom);
        }
      });
      return bottom;
    }

    updatePin() {
      if (!this.isConnected) return;
      const top = Math.round(ProductsToolbar.pinnedBottom());
      const pin = !window.matchMedia('(min-width: 1024px)').matches
        && this.spacer.getBoundingClientRect().top < top;

      if (pin !== this.classList.contains('is-pinned')) {
        // Measured before pinning, while the bar still has its in-flow height.
        this.spacer.style.height = pin ? `${this.offsetHeight + parseFloat(getComputedStyle(this).marginBottom)}px` : '';
        this.classList.toggle('is-pinned', pin);
      }
      this.style.setProperty('--products-toolbar-top', `${top}px`);
    }

    /**
     * Brings "Alla produkter" (or the results, where there is no heading) to
     * just under whatever is pinned at the top - the header when it is fixed,
     * and below lg the pinned toolbar too. One implementation for the theme's
     * filter re-render and the in-collection search alike.
     */
    static scrollToResults(correcting = false) {
      const anchor = document.querySelector('.cc-collection-products .divider-heading')
        || document.getElementById('filter-results');
      if (!anchor) return;

      let offset = ProductsToolbar.pinnedBottom();
      const bar = document.getElementById('products-toolbar');
      if (bar && !window.matchMedia('(min-width: 1024px)').matches) offset += bar.offsetHeight;

      const top = anchor.getBoundingClientRect().top;

      // Always brought to the top, even when it is already on screen further
      // down - leaving it where it was read as the page not responding.
      if (Math.abs(top - offset - 12) <= 4) return;

      if (correcting) {
        window.scrollTo({ top: top + window.scrollY - offset - 12 });
        return;
      }

      window.scrollTo({ top: top + window.scrollY - offset - 12, behavior: 'smooth' });

      // Anything that still re-lays the page while the scroll runs (lazy
      // images, a late listener) moves the heading out from under the target,
      // so measure once more when it ends and settle the difference.
      clearTimeout(ProductsToolbar.correctTimer);
      const correct = () => {
        clearTimeout(ProductsToolbar.correctTimer);
        window.removeEventListener('scrollend', correct);
        ProductsToolbar.scrollToResults(true);
      };
      window.addEventListener('scrollend', correct, { once: true });
      ProductsToolbar.correctTimer = setTimeout(correct, 1200);
    }

    init() {
      this.filtersComponent = document.querySelector('facet-filters');
      this.filtersColumn = document.querySelector('.main-products-grid__filters');
      this.layoutSwitcher = this.querySelector('.products-toolbar__layout');
      this.compareToggle = document.getElementById('compare-switch');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = document.querySelector('.main-products-grid');

      if (this.filtersColumn) {
        this.filterToggle = this.querySelector('.js-toggle-filters');
        if (this.filterToggle) {
          this.filterToggle.addEventListener('click', this.handleFilterToggle.bind(this));

          // Below lg the drawer can close without going back through
          // toggleFilters - Escape, the X, the overlay, or "Visa X resultat" all
          // call SideDrawer.close() directly - which left the trigger reporting
          // aria-expanded="true" over a shut drawer. SideDrawer announces its own
          // close, so resync from that.
          this.filtersComponent?.addEventListener('on:facet-filters:after-close', () => {
            if (theme.mediaMatches.lg) return;
            this.filtersOpen = false;
            this.filterToggle.classList.remove('is-active');
            this.filterToggle.setAttribute('aria-expanded', 'false');
          });
        }

        // Desktop: clicking the dim backdrop closes the filter
        this.filtersColumn.addEventListener('click', (e) => {
          if (theme.mediaMatches.lg && this.filtersOpen && e.target === this.filtersColumn) {
            this.toggleFilters();
          }
        });

        // X / footer close button: clear sort mode and close on desktop
        document.querySelectorAll('.js-close-drawer').forEach((btn) => {
          btn.addEventListener('click', (evt) => {
            const isFilterCloseButton = this.filtersComponent && btn.closest('facet-filters') === this.filtersComponent;
            if (this.filtersComponent) this.filtersComponent.classList.remove('is-sort-mode');
            this.setScrollLock(false);
            if (isFilterCloseButton && !theme.mediaMatches.lg) {
              evt.stopPropagation();
              this.filtersComponent.close();
            }
            if (theme.mediaMatches.lg && this.filtersOpen) {
              this.closeDesktopFilters();
            }
          });
        });
      }

      // Sortering button: custom-select.js opens dropdown on mousedown, so intercept mousedown + click
      if (this.filtersComponent && this.sortBy) {
        // Below lg the sort button opens the filter drawer as a dialog, so say
        // so; at lg+ it opens custom-select's listbox again, as the markup says.
        this.sortButton = this.sortBy.querySelector('.custom-select__btn');
        if (this.sortButton) {
          const lgQuery = window.matchMedia('(min-width: 1024px)');
          const syncSortAria = () => {
            if (lgQuery.matches) {
              this.sortButton.setAttribute('aria-haspopup', 'listbox');
              this.sortButton.removeAttribute('aria-controls');
            } else {
              this.sortButton.setAttribute('aria-haspopup', 'dialog');
              this.sortButton.setAttribute('aria-controls', this.filtersComponent.id);
            }
          };
          syncSortAria();
          lgQuery.addEventListener('change', syncSortAria);
        }

        this.sortBy.addEventListener('mousedown', (e) => {
          if (theme.mediaMatches.lg) return;
          e.stopImmediatePropagation();
          e.preventDefault();
        }, true);

        // Space and the arrow keys would open custom-select's own listbox
        // instead of the drawer. Only below lg - at lg+ that listbox is the sort.
        this.sortBy.addEventListener('keydown', (e) => {
          if (theme.mediaMatches.lg || ![' ', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
          e.stopImmediatePropagation();
          e.preventDefault();
          if (e.key === ' ') this.sortButton?.click();
        }, true);

        this.sortBy.addEventListener('click', (e) => {
          // lg+ keeps the sidebar filters on screen, so sort uses its own native
          // dropdown rather than taking the drawer over in sort mode.
          if (theme.mediaMatches.lg) return;
          e.stopImmediatePropagation();
          e.preventDefault();
          this.filtersComponent.classList.add('is-sort-mode');
          // The sort button is the opener, so closing hands focus back to it.
          this.filtersComponent.open(this.sortButton);
          this.sortButton?.setAttribute('aria-expanded', 'true');
          this.setScrollLock(true);
        }, true);

        // However the drawer closes, the sort button stops reporting it open.
        this.filtersComponent.addEventListener('on:facet-filters:after-close', () => {
          if (!theme.mediaMatches.lg) this.sortButton?.setAttribute('aria-expanded', 'false');
        });
      }

      // Track drawer open/close to manage scroll lock
      if (this.filtersComponent) {
        const observer = new MutationObserver(() => {
          const drawerOpen = this.filtersComponent.getAttribute('aria-hidden') === 'false';
          // Only lock scroll on mobile — on desktop we handle it in toggleFilters
          if (!theme.mediaMatches.lg) {
            this.setScrollLock(drawerOpen);
          }
        });
        observer.observe(this.filtersComponent, { attributes: true, attributeFilter: ['aria-hidden'] });
      }

      if (this.layoutSwitcher) {
        this.layoutSwitcher.addEventListener('change', this.handleLayoutChange.bind(this));

        const layout = theme.storageUtil.get('products-layout');
        if (layout && layout === 'list') {
          const toggle = document.getElementById(`${layout}-layout-opt`);
          if (toggle) toggle.click();
          this.handleLayoutChange(null, layout);
        }
      }

      // Compare toggle — guarded against null (this theme doesn't use compare)
      if (this.compareToggle) {
        this.compareToggle.addEventListener('change', this.toggleCompare.bind(this));
        const isChecked = theme.storageUtil.get('compare-toggle', true);
        if (isChecked !== null) {
          this.toggleCompare(null, isChecked, true);
        } else {
          this.toggleCompare(null, this.compareToggle.checked, false);
        }
        setTimeout(() => {
          this.classList.add('transition-ready');
          if (this.productsGrid) this.productsGrid.classList.add('transition-ready');
        }, 500);
      } else if (theme.settings && theme.settings.compareToggle === 'none') {
        this.toggleCompare(null, true, false);
      }

      if (!this.filtersComponent && this.sortBy) {
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }
    }

    /**
     * Locks/unlocks body scroll when drawer opens/closes.
     */
    setScrollLock(locked) {
      document.documentElement.classList.toggle('drawer-open', locked);
    }

    /**
     * Updates the UI to reflect the compare state
     */
    toggleCompare(evt, checked = null, persist = true) {
      const isChecked = evt ? evt.target.checked : checked;
      if (this.productsGrid) this.productsGrid.dataset.compare = isChecked;

      const compareButton = document.getElementById('compare-drawer-open');
      if (compareButton) compareButton.classList.toggle('is-out', !isChecked);

      if (!evt && this.compareToggle) this.compareToggle.checked = isChecked;

      if (persist && !Shopify.designMode) {
        theme.storageUtil.set('compare-toggle', isChecked);
      }

      if (isChecked) {
        const resourcesTag = document.getElementById('compare-resources');
        if (resourcesTag) {
          const resources = JSON.parse(resourcesTag.textContent);
          resources.forEach((resource) => {
            if (resource.type === 'js') {
              const script = document.createElement('script');
              script.src = resource.path;
              document.head.appendChild(script);
            } else if (resource.type === 'css') {
              const link = document.createElement('link');
              link.href = resource.path;
              link.type = 'text/css';
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
          });
          resourcesTag.remove();
        }
      }
    }

    /**
     * Opens the filter drawer in filter mode instead of leaving it in sort mode.
     */
    handleFilterToggle() {
      const wasSortMode = this.filtersComponent && this.filtersComponent.classList.contains('is-sort-mode');

      if (this.filtersComponent) {
        this.filtersComponent.classList.remove('is-sort-mode');
      }

      // If the shared drawer is already open in sort mode, switch content in place.
      if (wasSortMode) {
        if (theme.mediaMatches.lg) {
          if (!this.filtersOpen) this.toggleFilters();
        } else {
          this.filtersComponent.open(this.filterToggle);
          this.setScrollLock(true);
        }
        return;
      }

      this.toggleFilters();
    }

    /**
     * Toggles open/closed state of the filters on desktop.
     */
    toggleFilters() {
      this.filterToggle.classList.toggle('is-active');
      this.filtersOpen = this.filterToggle.classList.contains('is-active');
      this.filterToggle.setAttribute('aria-expanded', this.filtersOpen);

      if (theme.mediaMatches.lg) {
        if (this.filtersOpen) {
          this.openDesktopFilters();
        } else {
          this.closeDesktopFilters();
        }
      } else {
        this.filtersComponent.classList.remove('is-sort-mode');
        // Pass the trigger as the opener. SideDrawer.close() hands it to
        // removeTrapFocus, so without it focus lands on <body> when the drawer
        // closes instead of returning to the button that opened it.
        this.filtersComponent.open(this.filterToggle);
        this.setScrollLock(true);
      }
    }

    openDesktopFilters() {
      clearTimeout(this.desktopCloseTimer);
      this.filtersColumn.classList.remove('lg:js-hidden');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.filtersColumn.classList.add('is-visible');
        });
      });
      this.setScrollLock(true);
    }

    closeDesktopFilters() {
      clearTimeout(this.desktopCloseTimer);
      this.filtersOpen = false;
      if (this.filterToggle) {
        this.filterToggle.classList.remove('is-active');
        this.filterToggle.setAttribute('aria-expanded', 'false');
      }
      this.filtersColumn.classList.remove('is-visible');
      this.setScrollLock(false);
      this.desktopCloseTimer = setTimeout(() => {
        this.filtersColumn.classList.add('lg:js-hidden');
      }, this.desktopDrawerTransitionMs);
    }

    handleLayoutChange(evt, layout) {
      if (this.productsGrid) this.productsGrid.dataset.layout = layout || evt.target.value;
      if (!layout) theme.storageUtil.set('products-layout', evt.target.value);
    }

    static handleSortByChange(evt) {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('sort_by', evt.detail.selectedValue);
      urlObj.hash = 'products-toolbar';
      window.location.href = urlObj.toString();
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}
